/**
 * Enum values mirror the `squareup.cash.blockuserjourneys.api.v1` proto enums
 * (`RecordCompletedJourneyRequest`) so recorded journeys can be federated into the
 * Cash User Journeys system without translation.
 */
export enum JourneyOutcome {
  UNSPECIFIED = 'JOURNEY_OUTCOME_UNSPECIFIED',
  SUCCEEDED = 'JOURNEY_OUTCOME_SUCCEEDED',
  FAILED = 'JOURNEY_OUTCOME_FAILED',
  USER_CANCELED = 'JOURNEY_OUTCOME_USER_CANCELED',
  PROCESS_CRASHED = 'JOURNEY_OUTCOME_PROCESS_CRASHED',
  PROCESS_KILLED = 'JOURNEY_OUTCOME_PROCESS_KILLED',
  TERMINATED = 'JOURNEY_OUTCOME_TERMINATED',
}

export enum JourneyTerminationReason {
  UNSPECIFIED = 'JOURNEY_TERMINATION_REASON_UNSPECIFIED',
  TIMEOUT_WITH_ACTIVE_USER = 'JOURNEY_TERMINATION_REASON_TIMEOUT_WITH_ACTIVE_USER',
  TIMEOUT_WITH_IDLE_USER = 'JOURNEY_TERMINATION_REASON_TIMEOUT_WITH_IDLE_USER',
  JOURNEY_RESTARTED = 'JOURNEY_TERMINATION_REASON_JOURNEY_RESTARTED',
  MISSING_BLOCKER_FLOW_OUTCOME = 'JOURNEY_TERMINATION_REASON_MISSING_BLOCKER_FLOW_OUTCOME',
}

export type UserJourney = {
  /** Unique identifier for this specific journey instance */
  id: string
  /** Name of the journey (e.g. "afterpay-checkout") */
  name: string
  /** Timestamp in milliseconds when the journey was started */
  created: number
  /** Single variant identifier for the cohort/experiment that affected this journey */
  variant?: string
  /** Frustration signals — unexpected bad events that indicate poor user experience (e.g. "server-network-error") */
  frustrations: string[]
  /** Friction signals — expected events that may indicate issues at scale (e.g. "card-declined") */
  frictions: string[]
  /** Custom "key:value" tags for additional journey metadata */
  tags: string[]
}

export type UserJourneyState = {
  /** Names of all in-flight journeys, oldest first; the active journey is the last entry */
  history: string[]
  /** Journey name to journey state */
  journeys: Record<string, UserJourney>
  /** The currently active journey */
  active?: string
}

export type UserJourneyAction =
  | {
      type: 'USER_JOURNEY_START'
      id: string
      name: string
    }
  | {
      type: 'USER_JOURNEY_RESUME'
      name: string
      /** The full prior journey instance (from the sessionStorage marker) to reinstate */
      journey: UserJourney
      /** The esid of the page load that started the journey — reporting keeps using it */
      esid: string
    }
  | {
      type: 'USER_JOURNEY_COMPLETE'
      name: string
      outcome: JourneyOutcome
      terminationReason?: JourneyTerminationReason
    }
  | {
      type: 'USER_JOURNEY_ADD_FRUSTRATION'
      name: string
      frustration: string
    }
  | {
      type: 'USER_JOURNEY_ADD_FRICTION'
      name: string
      friction: string
    }
  | {
      type: 'USER_JOURNEY_ADD_TAG'
      name: string
      tag: string
    }
  | {
      type: 'USER_JOURNEY_SET_VARIANT'
      name: string
      variant: string
    }

/** Notification delivered synchronously to subscribers on every dispatched journey action */
export type UserJourneyStateChange = {
  action: UserJourneyAction
  prevState: UserJourneyState
  state: UserJourneyState
}

export type UserJourneyListener = (change: UserJourneyStateChange) => void

export type UserJourneyContextProps = {
  /** Whether user journey tracking is enabled (false outside the provider) */
  enabled: boolean
  /** Starts a new journey with the given name */
  startJourney: (name: string) => void
  /**
   * Reinstates a journey instance from a previous page load (same id/created/signals),
   * e.g. after an intentional cross-origin handoff. No-ops if a journey with that name
   * is already in flight. `esid` is the session id the journey started under.
   */
  resumeJourney: (journey: UserJourney, esid: string) => void
  /** Completes the named journey with the given outcome and optional termination reason */
  completeJourney: (name: string, outcome: JourneyOutcome, terminationReason?: JourneyTerminationReason) => void
  /** Adds a frustration signal to the named journey */
  addFrustration: (name: string, frustration: string) => void
  /** Adds a friction signal to the named journey */
  addFriction: (name: string, friction: string) => void
  /** Adds a tag to the named journey */
  addTag: (name: string, tag: string) => void
  /** Sets the variant for the named journey */
  setVariant: (name: string, variant: string) => void
  /** Subscribes to journey state changes; returns an unsubscribe function */
  subscribe: (listener: UserJourneyListener) => () => void
  /** The currently active journey */
  active?: UserJourney
  /** All in-flight journeys, oldest first; includes the active journey last */
  history?: UserJourney[]
}
